import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { fetchPopupNews, dismissPopupNews, NewsArticle } from '../../services/api/newsService';
import { useMetaLang } from '../../i18n/metaReviewLang';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}.${m}.${day}`;
}

/**
 * ログイン後の home で表示する「Tasky 新着情報」ポップアップ。
 *
 * - 管理画面で「ポップアップ表示」を ON にした公開ニュース（/news/popup）を取得
 * - そのユーザーが非表示にしたニュースはサーバー側で除外済み（端末をまたいで引き継ぐ）
 * - 直近 N 件を横スクロールカルーセルで表示（本文はモーダル内で完結）
 * - 閉じる / 最後のページの「閉じる」で、表示中のニュースをユーザーごとに非表示登録
 *   （POST /news/dismiss）。以降そのユーザーには表示されない。
 *
 * 既存の UpdateAnnouncementModal（Sanity 由来のプロダクトアップデート）とは別系統。
 * 同時に未読となる稀なケースでは、こちらを手前(z-10001)に表示する。
 */
const NewsPopupModal: React.FC = () => {
  const [lang] = useMetaLang();
  const [items, setItems] = useState<NewsArticle[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const news = await fetchPopupNews(5);
      if (!cancelled) setItems(news);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [items]);

  // サーバーが非表示分を除外して返すため、items が 1 件でもあれば表示する。
  // News content is authored in Japanese today. Suppress the automatic popup
  // during an English Meta-review recording; the Home news list uses safe
  // English labels without mutating or dismissing the original articles.
  const hasUnread = lang !== 'en' && !dismissed && items.length > 0;

  const markAsSeen = useCallback(() => {
    const ids = items.map((n) => n.id);
    setDismissed(true);
    // 表示中のニュースをこのユーザーの非表示として記録（ベストエフォート）
    dismissPopupNews(ids);
  }, [items]);

  const goPrev = useCallback(() => setActiveIndex((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(
    () => setActiveIndex((i) => Math.min(items.length - 1, i + 1)),
    [items.length],
  );

  useEffect(() => {
    if (!hasUnread) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') markAsSeen();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [hasUnread, goPrev, goNext, markAsSeen]);

  if (!hasUnread || items.length === 0) {
    return null;
  }

  const isFirst = activeIndex === 0;
  const isLast = activeIndex === items.length - 1;

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="news-popup-title"
      // 背景（モーダル外）クリックで閉じる。内側のクリックは currentTarget 判定で無視する
      onClick={(e) => {
        if (e.target === e.currentTarget) markAsSeen();
      }}
    >
      {/* 低い画面でも閉じるボタンとフッターが画面内に収まるよう、高さを画面に合わせて制限し
          スライド部分だけをスクロールさせる */}
      <div
        className="relative w-full rounded-2xl bg-white shadow-2xl flex flex-col max-h-[calc(100dvh-2rem)]"
        style={{ maxWidth: '800px' }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={markAsSeen}
          aria-label="閉じる"
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-600 shadow-sm hover:bg-white hover:text-gray-900 transition-colors"
        >
          <X size={20} />
        </button>

        {/* Carousel track */}
        <div className="flex-1 min-h-0 overflow-hidden rounded-t-2xl flex">
          <div
            className="flex w-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${activeIndex * 100}%)` }}
          >
            {items.map((n) => (
              <div
                key={n.id}
                className="overflow-y-auto"
                style={{ flex: '0 0 100%', minWidth: '100%', maxWidth: '100%' }}
              >
                <NewsSlide news={n} />
              </div>
            ))}
          </div>
        </div>

        {/* Prev / Next overlay buttons */}
        {!isFirst && (
          <button
            type="button"
            onClick={goPrev}
            aria-label="前のお知らせ"
            className="absolute left-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-md hover:bg-white transition"
          >
            <ChevronLeft size={20} />
          </button>
        )}
        {!isLast && (
          <button
            type="button"
            onClick={goNext}
            aria-label="次のお知らせ"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-gray-700 shadow-md hover:bg-white transition"
          >
            <ChevronRight size={20} />
          </button>
        )}

        {/* Footer: pagination + CTA */}
        <div className="shrink-0 px-6 pb-5 border-t border-gray-100">
          <div className="flex justify-center gap-1.5 pt-3 pb-4">
            {items.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIndex(i)}
                aria-label={`スライド ${i + 1}`}
                className={`h-2 rounded-full transition-all ${
                  i === activeIndex ? 'w-6 bg-gray-900' : 'w-2 bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center justify-end gap-2">
            {isLast ? (
              <button
                type="button"
                onClick={markAsSeen}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:opacity-90 transition-opacity"
              >
                閉じる
              </button>
            ) : (
              <button
                type="button"
                onClick={goNext}
                className="px-5 py-2.5 rounded-full text-sm font-semibold bg-gray-900 text-white hover:opacity-90 transition-opacity"
              >
                次へ
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// Slide（サムネ任意 + タイトル + HTML本文。本文はモーダル内でスクロール）

interface NewsSlideProps {
  news: NewsArticle;
}

const NewsSlide: React.FC<NewsSlideProps> = ({ news }) => {
  // 本文は管理画面で入力された HTML。描画前にサニタイズする（毎レンダーの
  // 再計算を避けるため元 HTML をキーにメモ化）。
  const sanitizedContent = useMemo(() => sanitizeHtml(news.content), [news.content]);

  return (
    <div className="px-5 sm:px-7 pt-12 pb-2">
      {/* トップ動画があれば画像の代わりに自動再生（自動再生はミュートが必須）。
          poster にサムネイルを使い、読み込み中も従来と同じ絵が出るようにする。 */}
      {news.hero_video_url ? (
        <video
          data-testid="news-hero-video"
          src={news.hero_video_url}
          poster={news.thumbnail_url ?? undefined}
          autoPlay
          muted
          loop
          playsInline
          controls
          preload="metadata"
          className="w-full max-h-[min(20rem,35dvh)] rounded-xl mb-4 bg-black object-contain"
        />
      ) : (
        news.thumbnail_url && (
          <img
            data-testid="news-hero-image"
            src={news.thumbnail_url}
            alt=""
            className="w-full h-[min(20rem,35dvh)] object-contain rounded-xl mb-4"
          />
        )
      )}
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-block text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#dff5ed] text-[#2a8c6a]">
          新着情報
        </span>
        <span className="text-xs font-medium text-gray-500">
          {formatDate(news.published_at)}
        </span>
      </div>
      <h2
        id="news-popup-title"
        className="text-xl font-extrabold leading-snug text-gray-900 mb-3"
      >
        {news.title}
      </h2>
      {/* 本文は管理画面で入力された HTML。NewsManagementSection / NewsDetailPage と同様に描画する */}
      <div
        className="text-sm text-gray-600 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </div>
  );
};

export default NewsPopupModal;
